import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Flag, Loader2 } from "lucide-react";

const MOTIVOS = [
  { value: "contenido ofensivo", label: "Contenido ofensivo" },
  { value: "spam o publicidad", label: "Spam o publicidad" },
  { value: "información falsa o peligrosa", label: "Información falsa o peligrosa" },
  { value: "otro", label: "Otro motivo" },
];

export const ReportButton = ({ targetType, targetId, iconOnly = true }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [motivo, setMotivo] = useState(MOTIVOS[0].value);
  const [detalle, setDetalle] = useState("");
  const [sending, setSending] = useState(false);

  const handleOpen = () => {
    if (!user) {
      toast.info("Inicia sesión para reportar contenido");
      navigate("/login");
      return;
    }
    setOpen(true);
  };

  const submit = async () => {
    setSending(true);
    try {
      const path = targetType === "comment" ? `/comments/${targetId}/report` : `/prayers/${targetId}/report`;
      const motivoFinal = detalle.trim() ? `${motivo}: ${detalle.trim()}` : motivo;
      const res = await api.post(path, { motivo: motivoFinal });
      toast.success(res.data?.detail || "Gracias por tu reporte");
      setOpen(false);
      setDetalle("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo enviar el reporte");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        title="Reportar contenido"
        aria-label="Reportar contenido"
        className="inline-flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground/70 transition-colors duration-150 hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        data-testid={`report-button-${targetType}-${targetId}`}
      >
        <Flag className="h-3.5 w-3.5" />
        {!iconOnly && <span className="ml-1 text-xs">Reportar</span>}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Reportar contenido</DialogTitle>
            <DialogDescription>
              Ayúdanos a mantener este espacio seguro y edificante. Nuestro equipo revisará tu reporte.
            </DialogDescription>
          </DialogHeader>
          <RadioGroup value={motivo} onValueChange={setMotivo} className="gap-3" data-testid="report-motivo-group">
            {MOTIVOS.map((m) => (
              <div key={m.value} className="flex items-center gap-2">
                <RadioGroupItem value={m.value} id={`motivo-${m.value}`} data-testid={`report-motivo-${m.value.split(" ")[0]}`} />
                <Label htmlFor={`motivo-${m.value}`} className="cursor-pointer text-sm font-normal">{m.label}</Label>
              </div>
            ))}
          </RadioGroup>
          <Textarea
            value={detalle}
            onChange={(e) => setDetalle(e.target.value)}
            placeholder="Detalles adicionales (opcional)"
            className="min-h-[70px]"
            maxLength={250}
            data-testid="report-detalle-textarea"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="report-cancel-button">Cancelar</Button>
            <Button onClick={submit} disabled={sending} data-testid="report-submit-button">
              {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Flag className="mr-2 h-4 w-4" />}
              Enviar reporte
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
